import { useVerificationsOverviewController } from "shared/queries/useVerificationsOverviewController";
import { VerificationNames, VerificationStatus } from "shared/types";
import { has, formatToCurrency } from "shared/utils";
import { Money } from "types/landlord/reports";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  ApplicantOrInvitee,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";
import { useAuthContext } from "providers/Auth";
import { HugeiconsIcon } from "@hugeicons/react";
import { MinusSignIcon } from "@hugeicons-pro/core-stroke-rounded";

const IN_PROGRESS_STATUSES = [
  VerificationStatus.Started,
  VerificationStatus.Submitted
];

const getUIStatus = ({
  verificationStatus,
  hasWarnings
}: {
  verificationStatus: VerificationStatus;
  hasWarnings: boolean;
}): VerificationStatus => {
  if (hasWarnings) return VerificationStatus.FinishedWithWarnings;
  if (IN_PROGRESS_STATUSES.includes(verificationStatus))
    return VerificationStatus.Started;
  return verificationStatus;
};

const DataPoint = ({
  val,
  applicant,
  hasAuditFlagEnabled
}: {
  val: string | number | undefined | null | Money;
  applicant: ApplicantOrInvitee;
  hasAuditFlagEnabled: boolean;
}) => {
  if (has(val) && typeof val === "object" && "cents" in val)
    return <span>{formatToCurrency(val)}</span>;

  if (hasAuditFlagEnabled)
    return (
      <AuditSummaryFlag
        audit_summary_flags={
          "audit_summary_flags" in applicant
            ? applicant.audit_summary_flags
            : undefined
        }
      />
    );

  return (
    <HugeiconsIcon
      icon={MinusSignIcon}
      size={20}
      className="text-content-100"
    />
  );
};

export const CombinedIncomeReportSection = ({
  applicant,
  verification,
  hasAuditFlagEnabled
}: {
  applicant: ApplicantOrInvitee;
  hasAuditFlagEnabled: boolean;
  verification: Verification;
}) => {
  const { permissions } = useAuthContext();

  const { verificationsOverviewMeta } = useVerificationsOverviewController({
    applicationId: Number(applicant.application_id),
    applicantId: Number(applicant.id)
  });

  const status = getUIStatus({
    verificationStatus: verification.status,
    hasWarnings:
      verificationsOverviewMeta.data?.[VerificationNames.CombinedIncome]
        ?.has_warnings ?? false
  });

  return (
    <>
      {/* Empty cell for all rows */}
      <Cell className="bg-background-900" />
      <Cell
        isDataCell
        className="relative"
        showRestrictedFlag={!permissions.canAccessReports.incomeReport}
        isPermitted={permissions.canAccessReports.incomeReport}
      >
        {/* Gross income (Verified) */}
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
        <ReportStatusBadge verificationStatus={status} />
      </Cell>
      {/* Net income (Verified) */}
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_net_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
      {/* Gross income (Self-reported) */}
      <Cell isDataCell isPermitted={permissions.canAccessReports.incomeReport}>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.self_reported_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
    </>
  );
};
